import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, parseJSON } from "date-fns";
import { Box, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChatIcon from "@icons/myicons/chat.svg?react";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import {
  getNumberOfLotsByInvestmentAndBookingType,
  getValueByInvestmentAndBookingType,
  toCurrency,
  toPercent,
} from "@helpers";
import { BookingType, TableConfig, Tooltip } from "@components";
import { TransactionStatusIcon } from "./TransactionStatusIcon";
import { BookingsDetailsMobileView } from "./BookingsDetailsMobileView";
import { BookingActionMenu } from "./InvestmentTable/InvestmentDetailModal/BookingActionMenu";
import { isBookingEditable } from "./InvestmentTable/InvestmentDetailModal/EditBookingModal";
import {
  TableBookingData,
  TypographyColored,
  ValueDateWrapper,
} from "./BookingsSingleInvestmentTable";

export type BookingsTableAdditionalProps = {
  currencySymbol?: string;
  onEditBooking: (bookingId: number) => void;
  onDeleteBooking: (bookingId: number) => void;
};

type RendererProps = {
  row: TableBookingData;
} & Partial<BookingsTableAdditionalProps>;

const TypeAndDateCell = ({ row }: RendererProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconStyle = {
    width: "16px",
    height: "16px",
    color: theme.palette.text.secondary,
    cursor: "help",
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <BookingType type={row.bookingType} />
        {row.isCancelled && (
          <Tooltip text={t("investment.protocolTable.cancelled")}>
            <Box display="flex">
              <CancelledIcon style={iconStyle} />
            </Box>
          </Tooltip>
        )}
        {row.comment && (
          <Tooltip
            title={t("investment.protocolTable.comment")}
            text={row.comment}
          >
            <Box display="flex">
              <ChatIcon style={iconStyle} />
            </Box>
          </Tooltip>
        )}
      </Box>
      <ValueDateWrapper>
        <TransactionStatusIcon
          dataConsistencyStatus={row.dataConsistencyStatus}
          isExternalID={row.isExternalID}
        />
        <Typography variant="body2" ml={2} color="textSecondary">
          {format(parseJSON(row.valueDate), "dd.MM.yyyy")}
        </Typography>
      </ValueDateWrapper>
    </>
  );
};

const AmountCell = ({ row }: RendererProps) => {
  const { t } = useTranslation();
  const numberOfLotsText = getNumberOfLotsByInvestmentAndBookingType({
    value: row.numberOfLots,
    investmentType: row.investmentType,
    bookingType: row.bookingType,
  });
  const valueText = getValueByInvestmentAndBookingType({
    quoteNotation: row.quoteNotation,
    value: row.securityPrice,
    investmentType: row.investmentType,
    bookingType: row.bookingType,
    percentageRange: "0-100",
    currency: row.currency,
  });
  const amountText = numberOfLotsText
    ? numberOfLotsText + " " + valueText
    : valueText;

  return (
    <>
      <Typography variant="body1">{amountText}</Typography>
      {row.priceChange ? (
        <div>
          <Typography variant="body2" color="textPrimary" display="inline">
            {t("investment.protocolTable.gainersAndLosersLabel")}:{" "}
          </Typography>
          <TypographyColored
            variant="body2"
            lots={row.priceChange}
            display="inline"
          >
            {toPercent(row.priceChange)}
          </TypographyColored>
        </div>
      ) : null}
    </>
  );
};

const ExpensesCell = ({ row, currencySymbol }: RendererProps) => (
  <Typography variant="body2">
    {toCurrency(row.expenses, currencySymbol)}
  </Typography>
);

const TotalSumCell = ({ row, currencySymbol }: RendererProps) => (
  <>
    <Typography variant="body2">
      {toCurrency(row.amount, currencySymbol)}
    </Typography>
    <Typography variant="body1">
      {row.bookingType === "sell" && row.grossGain
        ? toCurrency(row.grossGain, currencySymbol)
        : null}
    </Typography>
  </>
);

const ActionsCell = ({
  row,
  onEditBooking,
  onDeleteBooking,
}: RendererProps) => (
  <BookingActionMenu
    showEdit={isBookingEditable(row.bookingType)}
    onEdit={() => onEditBooking?.(row.id)}
    onDelete={() => onDeleteBooking?.(row.id)}
  />
);

type SortArgs = {
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort: (field: string) => void;
  onEditBooking: (bookingId: number) => void;
  onDeleteBooking: (bookingId: number) => void;
};

export const useInvestmentBookingsTableConfig = ({
  sortField,
  sortDirection,
  onSort,
  onEditBooking,
  onDeleteBooking,
}: SortArgs): TableConfig<TableBookingData> => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  return useMemo(
    () => ({
      id: "investment-bookings-table",
      getRowId: (row) => row.id,
      isRowExpandable: isSm ? () => true : undefined,
      arrowPosition: "first",
      sorting:
        sortField && sortDirection && sortDirection !== "NONE"
          ? { field: sortField, direction: sortDirection }
          : undefined,
      onSort,
      childrenRenderer: isSm
        ? (row) => (
            <BookingsDetailsMobileView
              {...row}
              onEdit={onEditBooking}
              onDelete={onDeleteBooking}
            />
          )
        : undefined,
      columns: [
        {
          id: "valueDate",
          sortKey: "valueDate",
          title: t("investment.protocolTable.bookingType"),
          renderer: TypeAndDateCell,
          sortable: true,
        },
        {
          id: "price",
          sortKey: "securityPrice",
          title: t("investment.protocolTable.amount"),
          renderer: AmountCell,
          sortable: true,
        },
        {
          id: "expenses",
          title: t("investment.protocolTable.expenses"),
          renderer: ExpensesCell,
          hidden: isSm,
        },
        {
          id: "totalSum",
          sortKey: "amount",
          title: t("investment.protocolTable.totalSum"),
          renderer: TotalSumCell,
          sortable: true,
          align: isSm ? "right" : "left",
        },
        {
          id: "actions",
          title: t("investment.protocolTable.actions.title"),
          renderer: ActionsCell,
          align: "right",
          hidden: isSm,
        },
      ],
    }),
    [t, isSm, sortField, sortDirection, onSort, onEditBooking, onDeleteBooking]
  );
};
